/**
 * WikipediaPreviewCard Component
 * 
 * Displays a preview card for Wikipedia pages that don't exist as issues yet.
 * Similar to IssueCard but simplified - shows title, thumbnail, and extract.
 */

import React from "react";
import { WikipediaPreviewData } from "hooks/useIssuePreview";
import { getLinkIcon } from "util/transformHtmlContent";
import EntityThumbnail from "components/Entities/EntityThumbnail";
import PostCreateButton from "ui/PostCreateButton";
import { Issue, User, WikiSuggestion } from "util/constants";

interface WikipediaPreviewCardProps {
  /** Wikipedia preview data to display */
  data: WikipediaPreviewData;
  /** Current logged-in user (to show create button) */
  currentUser?: User | null;
  /** Callback when card is clicked */
  onClick?: () => void;
  /** Callback when PostCreateButton opens modal (to close preview) */
  onPostCreateClick?: () => void;
}

/**
 * Card component for displaying Wikipedia page previews
 */
const WikipediaPreviewCard: React.FC<WikipediaPreviewCardProps> = ({ data, currentUser, onClick, onPostCreateClick }) => {
  
  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Don't trigger if clicking on links or buttons
    const element = event.target as HTMLElement;
    if (element.tagName.toLowerCase() === "a" || element.tagName.toLowerCase() === "button" || element.closest("button")) {
      return;
    }
    
    if (onClick) {
      onClick();
    }
  };
  
  // Convert Wikipedia data to WikiSuggestion format for SpillTheTeaModal
  const wikiSuggestion: WikiSuggestion = {
    type: "wiki",
    name: data.title,
    link: data.wikipediaUrl,
    thumbnail: data.thumbnail || "",
    cname: data.canonicalTitle,
    subject_properties: {
      title: data.title,
      displaytitle: data.displayTitle,
      canonicalTitle: data.canonicalTitle,
      extract: data.extract,
      description: data.description,
      thumbnail: data.thumbnail,
      wikipediaUrl: data.wikipediaUrl
    },
    // Normalize nullable description to an optional string for suggestion payloads.
    short_description: data.description ?? undefined
  };

  return (
    <div className="card-container my-1">
      <article>
        <div className="rounded-lg flex flex-col px-2 md:px-3 py-1 relative card-main bg-transparent">
          <div className="card-content">
            {/* Title */}
            <div className="card-header flex flex-row justify-between">
              <span className="card-title">
                {data.title}
              </span>
            </div>

            {/* Short description if available */}
            {data.description && (
              <div className="card-tags flex flex-row justify-between my-1 items-center">
                <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub italic">
                  {data.description}
                </span>
              </div>
            )}

            {/* Extract + Thumbnail */}
            <div 
              className="card-description flex flex-col-reverse gap-2 cursor-pointer" 
              onClick={handleCardClick}
            >
              {/* Extract text */}
              <div>
                <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                  {data.extract}
                </p>
                
                {/* Wikipedia link */}
                <div className="flex flex-col gap-2 my-4">
                  <div className="inline-flex items-center gap-1">
                    <a
                      className="external-link text-xs inline-flex items-center"
                      href={data.wikipediaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Open Wikipedia"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {getLinkIcon("wikipedia", data.wikipediaUrl)}
                    </a>
                    <a
                      className="external-link text-xs line-clamp-1 break-all"
                      href={data.wikipediaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {data.canonicalTitle.replace(/_/g, " ")}
                    </a>
                  </div>
                </div>
              </div>

              {/* Thumbnail */}
              {data.thumbnail && (
                <div className="flex flex-row cursor-pointer">
                  <EntityThumbnail
                    url={data.thumbnail}
                    urlType="img"
                    isProfilePage={false}
                    onCardClick={handleCardClick}
                    referenceLink={data.wikipediaUrl}
                  />
                </div>
              )}
            </div>

            {/* Create Post Button - only show for logged-in users */}
            {currentUser && (
              <div 
                className="mt-4 pt-4 border-t border-eoe-border-main dark:border-eoe-dark-border-main" 
                onClick={(e) => {
                  e.stopPropagation();
                  // Close preview when button is clicked
                  if (onPostCreateClick) {
                    onPostCreateClick();
                  }
                }}
              >
                <div className="flex flex-col gap-2">
                  <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    Add this issue to Societea by creating a post.
                  </p>
                  <PostCreateButton
                    buttonText="Spill the tea"
                    tooltip="Add a post about this issue"
                    className="w-full justify-center"
                    initialIssue={wikiSuggestion as unknown as Issue}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </article>
    </div>
  );
};

export default WikipediaPreviewCard;

